
import React from 'react';
import { VideoEditorLayout } from '@/components/layout/VideoEditorLayout';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { AutoplayModalPremium } from '@/components/modals/AutoplayModalPremium';
import { SideBySideEditor } from '@/components/video-editor/SideBySideEditor';
import { VideoFeatures } from '@/store/videoFeatures';
import { t } from '@/utils/i18n';
import { generateVideoEmbedCode, generateVideoJsCode } from '@/utils/embedDomain';
import { useCustomAuth } from '@/contexts/CustomAuthContext';

interface ModularVideoEditorContainerProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  currentFeature: string | null;
  setCurrentFeature: (featureId: string | null) => void;
  modalTitle: string;
  modalSubtitle?: string;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: React.Dispatch<React.SetStateAction<boolean>>;
  confirmNavigation: () => void;
  showEmbedModal: boolean;
  setShowEmbedModal: React.Dispatch<React.SetStateAction<boolean>>;
  isSaved: boolean;
  activeEditingPanel: string | null;
  onCloseEditor: () => void;
  onApplyChanges: () => void;
  children: React.ReactNode;
}

export const ModularVideoEditorContainer: React.FC<ModularVideoEditorContainerProps> = ({
  features,
  setFeature,
  currentFeature,
  setCurrentFeature,
  modalTitle,
  modalSubtitle,
  showUnsavedDialog,
  setShowUnsavedDialog,
  confirmNavigation,
  showEmbedModal,
  setShowEmbedModal,
  isSaved,
  activeEditingPanel,
  onCloseEditor,
  onApplyChanges,
  children,
}) => {
  const { user } = useCustomAuth();
  const userId = user?.id;

  // Mapeia dados da headline das features para props do CopyCodeModal. Após a
  // migration, `size` no state é `number` (px). Mantém fallback para string
  // legada (ex: 'medium'/'24') até dados antigos em cache de browser expirarem.
  const HEADLINE_LEGACY_SIZE: Record<string, number> = {
    small: 16, medium: 24, large: 32, xlarge: 40,
    pequeno: 16, medio: 24, grande: 32,
  };
  const resolveSize = (raw: any, defaultPx: number): number => {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string') {
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num > 0) return num;
      const legacy = HEADLINE_LEGACY_SIZE[raw.trim().toLowerCase()];
      if (legacy) return legacy;
    }
    return defaultPx;
  };
  const hl = features.headline;
  const headlineProps = {
    headlineText: hl?.headline?.text ?? '',
    subHeadlineText: hl?.subheadline?.text ?? '',
    headlineColor: hl?.headline?.color ?? '#ffffff',
    subHeadlineColor: hl?.subheadline?.color ?? '#e5e5e5',
    headlineSize: resolveSize(hl?.headline?.size, 24),
    subHeadlineSize: resolveSize(hl?.subheadline?.size, 16),
    headlineTypeFont: hl?.headline?.fontFamily ?? 'Inter',
    headlineFontWeight: hl?.headline?.fontWeight ?? 'normal',
    headlineShowThroughout: hl?.timing?.showThroughout,
    headlineInitialDelay: hl?.timing?.start,
    headlineEndDelay: hl?.timing?.end,
  };

  // Se qualquer recurso está ativo, usar SideBySideEditor (layout 40/60 com preview)
  const activeFeature = currentFeature || activeEditingPanel;
  
  if (activeFeature) {
    return (
      <VideoEditorLayout>
        <SideBySideEditor
          activeFeature={activeFeature}
          features={features}
          setFeature={setFeature}
          modalTitle={modalTitle}
          modalSubtitle={modalSubtitle}
          onClose={onCloseEditor}
          onApply={onApplyChanges}
        />

        {/* Renderizar children de forma oculta para manter portal mobile vivo */}
        <div className="hidden">
          {children}
        </div>

        <CopyCodeModal
          isOpen={showEmbedModal}
          onClose={() => setShowEmbedModal(false)}
          entityType="video"
          entityId={features.videoId}
          entityName="Vídeo"
          iframeCode={generateVideoEmbedCode(features.videoId)}
          jsCode={generateVideoJsCode(features.videoId)}
          userId={userId}
          haveHeadline={features.headline?.enabled}
          isHTMLSelected={features.headline?.htmlMode?.enabled ?? false}
          headlineHtmlCode={features.headline?.htmlMode?.code || ''}
          {...headlineProps}
        />

        <UnsavedChangesDialog
          isOpen={showUnsavedDialog}
          onClose={() => setShowUnsavedDialog(false)}
          onConfirmLeave={confirmNavigation}
        />
      </VideoEditorLayout>
    );
  }

  // Layout normal: nenhum recurso ativo
  return (
    <VideoEditorLayout>
      <div className="flex flex-col w-full">
        {/* Header section */}
        <div className="sticky top-0 z-10 bg-white border-b px-4">
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              const childType = child.type;
              if (childType === 'header' || 
                  (typeof childType === 'function' && 
                    ((childType as any).name === 'HeaderSection' || 
                     (childType as any).displayName === 'VideoEditorHeader'))) {
                return child;
              }
            }
            return null;
          })}
        </div>
        
        {/* Content section */}
        <div className="flex-1">
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              const childType = child.type;
              if (!(childType === 'header' || 
                   (typeof childType === 'function' && 
                     ((childType as any).name === 'HeaderSection' || 
                      (childType as any).displayName === 'VideoEditorHeader')))) {
                return child;
              }
            }
            return null;
          })}
        </div>
      </div>
      
      <CopyCodeModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        entityType="video"
        entityId={features.videoId}
        entityName="Vídeo"
        iframeCode={generateVideoEmbedCode(features.videoId)}
        jsCode={generateVideoJsCode(features.videoId)}
        userId={userId}
        haveHeadline={features.headline?.enabled}
        isHTMLSelected={features.headline?.htmlMode?.enabled ?? false}
        headlineHtmlCode={features.headline?.htmlMode?.code || ''}
        {...headlineProps}
      />
      
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        onConfirmLeave={confirmNavigation}
      />
    </VideoEditorLayout>
  );
};